if (!customElements.get('btl-banner-carousel')) {
  customElements.define('btl-banner-carousel', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.track = this.querySelector('[data-banner-track]');
      if (!this.track) return;
      this.slides = [...this.track.children];
      this.dots = [...this.querySelectorAll('[data-banner-dot]')];
      this.controls = this.querySelector('[data-banner-controls]');
      this.arrows = this.querySelector('[data-banner-arrows]');
      this.dotRow = this.querySelector('[data-banner-dots]');
      this.pauseButton = this.querySelector('[data-banner-pause]');
      this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.visible = false;
      this.paused = false;
      this.engaged = false;
      this.hovered = false;
      this.dragging = false;
      this.editorPaused = false;
      const options = { signal: this.controller.signal };
      this.addEventListener('click', event => {
        const direction = event.target.closest('[data-banner-direction]');
        const dot = event.target.closest('[data-banner-dot]');
        if (direction) this.go(this.active + Number(direction.dataset.bannerDirection));
        else if (dot) this.go(this.positions.findIndex(position => position.slide === Number(dot.dataset.bannerDot)));
        else if (event.target.closest('[data-banner-pause]')) {
          this.paused = !this.paused;
          this.pauseButton.setAttribute('aria-pressed', String(this.paused));
          this.pauseButton.textContent = this.paused ? 'Play' : 'Pause';
          this.pauseButton.setAttribute('aria-label', this.paused ? 'Play automatic banner scrolling' : 'Pause automatic banner scrolling');
          this.schedule();
        }
      }, options);
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => this.update());
      }, { ...options, passive: true });
      this.track.addEventListener('keydown', event => {
        let target;
        if (event.key === 'ArrowRight') target = this.active + 1;
        else if (event.key === 'ArrowLeft') target = this.active - 1;
        else if (event.key === 'Home') target = 0;
        else if (event.key === 'End') target = this.positions.length - 1;
        else return;
        event.preventDefault(); this.go(target);
      }, options);
      this.addEventListener('keydown', event => {
        const dot = event.target.closest('[data-banner-dot]');
        if (!dot) return;
        let target;
        if (event.key === 'ArrowRight') target = this.active + 1;
        else if (event.key === 'ArrowLeft') target = this.active - 1;
        else if (event.key === 'Home') target = 0;
        else if (event.key === 'End') target = this.positions.length - 1;
        else return;
        event.preventDefault(); this.go(target, true);
      }, options);
      this.addEventListener('focusin', () => { this.engaged = true; this.schedule(); }, options);
      this.addEventListener('focusout', event => { this.engaged = this.contains(event.relatedTarget); this.schedule(); }, options);
      this.addEventListener('mouseenter', () => { this.hovered = window.matchMedia('(hover: hover)').matches; this.schedule(); }, options);
      this.addEventListener('mouseleave', () => { this.hovered = false; this.schedule(); }, options);
      this.track.addEventListener('pointerdown', () => { this.dragging = true; this.schedule(); }, options);
      document.addEventListener('pointerup', () => { this.dragging = false; this.schedule(); }, options);
      document.addEventListener('pointercancel', () => { this.dragging = false; this.schedule(); }, options);
      document.addEventListener('visibilitychange', () => this.schedule(), options);
      this.motionPreference.addEventListener('change', () => this.schedule(), options);
      document.addEventListener('shopify:block:select', event => {
        const index = this.slides.findIndex(slide => slide.contains(event.target));
        if (index < 0) return;
        this.editorPaused = true;
        this.go(this.positions.findIndex(position => position.slide === index));
      }, options);
      document.addEventListener('shopify:block:deselect', event => {
        if (!this.contains(event.target)) return;
        this.editorPaused = false; this.schedule();
      }, options);
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.track);
      this.visibilityObserver = new IntersectionObserver(entries => {
        this.visible = entries.some(entry => entry.isIntersecting);
        this.schedule();
      }, { threshold: 0.2 });
      this.visibilityObserver.observe(this);
      this.measure();
    }
    measure() {
      const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      this.positions = [];
      this.slides.forEach((slide, index) => {
        const left = Math.max(0, Math.min(max, slide.offsetLeft - this.slides[0].offsetLeft));
        if (!this.positions.length || Math.abs(left - this.positions.at(-1).left) > 2) this.positions.push({ left, slide: index });
      });
      this.dots.forEach((dot, index) => { dot.hidden = !this.positions.some(position => position.slide === index); });
      if (this.controls) this.controls.hidden = this.positions.length < 2;
      if (this.arrows) this.arrows.hidden = this.positions.length < 2;
      this.update();
      this.schedule();
    }
    update() {
      const previous = this.active;
      this.active = this.positions.reduce((best, position, index, positions) =>
        Math.abs(position.left - this.track.scrollLeft) < Math.abs(positions[best].left - this.track.scrollLeft) ? index : best, 0);
      const selected = this.positions[this.active]?.slide;
      this.dots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === selected)));
      this.updateDots();
      if (this.pendingDotFocus === selected) {
        this.pendingDotFocus = undefined;
        this.dots[selected]?.focus();
      }
      if (previous !== undefined && previous !== this.active) this.schedule();
    }
    updateDots() {
      if (!this.dotRow) return;
      const start = Math.max(0, Math.min(this.active - 3, this.positions.length - 6));
      this.dotRow.style.width = Math.min(this.positions.length, 6) * 24 + 28 + 'px';
      this.dotRow.style.setProperty('--banner-dot-offset', -start * 24 + 'px');
      this.positions.forEach((position, index) => {
        const dot = this.dots[position.slide];
        const visible = index >= start && index < start + 6;
        dot.style.left = index * 24 + (index > this.active ? 28 : 0) + 'px';
        dot.style.setProperty('--banner-dot-scale', Math.max(.4, 1 - Math.max(0, Math.abs(index - this.active) - 1) * .2));
        dot.inert = !visible;
        dot.tabIndex = index === this.active ? 0 : -1;
        if (!visible && dot === document.activeElement) this.dots[this.positions[this.active].slide]?.focus();
      });
    }
    go(index, focusDot = false) {
      if (!this.positions?.length) return;
      const wrapped = (index + this.positions.length) % this.positions.length;
      if (focusDot) this.pendingDotFocus = this.positions[wrapped].slide;
      this.track.scrollTo({ left: this.positions[wrapped].left, behavior: this.motionPreference.matches ? 'auto' : 'smooth' });
      if (focusDot && wrapped === this.active) this.update();
      this.schedule();
    }
    schedule() {
      clearTimeout(this.timer);
      this.progress?.cancel();
      this.removeAttribute('data-banner-running');
      if (this.dataset.autoplay !== 'true' || !this.visible || document.hidden || this.paused || this.engaged || this.hovered || this.dragging || this.editorPaused || this.motionPreference.matches || !(this.positions?.length > 1)) return;
      const delay = Math.max(3000, Math.min(12000, Number(this.dataset.interval) || 5000));
      const fill = this.dots[this.positions[this.active].slide]?.querySelector('[data-banner-progress]');
      if (typeof fill?.animate === 'function') {
        this.setAttribute('data-banner-running', '');
        this.progress = fill.animate([{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }], { duration: delay, fill: 'forwards' });
      }
      this.timer = setTimeout(() => this.go(this.active + 1), delay);
    }
    disconnectedCallback() {
      this.controller?.abort();
      clearTimeout(this.timer);
      this.progress?.cancel();
      cancelAnimationFrame(this.scrollFrame);
      this.resizeObserver?.disconnect();
      this.visibilityObserver?.disconnect();
    }
  });
}
